import React, { useState, useEffect, useCallback } from 'react';
import { axiosAuth } from '../contexts/AuthContext';
import { Search, RefreshCw, Loader2, ShieldCheck, Mail, DollarSign, Package } from 'lucide-react';

const fmtDate = (s) => {
  if (!s) return '–';
  try { return new Date(s).toLocaleString(); } catch (_) { return s; }
};

const fmtCurrency = (n, currency = 'USD') => {
  if (n == null) return '–';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'USD').toUpperCase() }).format(n);
  } catch (_) {
    return `$${Number(n).toFixed(2)}`;
  }
};

// ===== USERS TAB =====
export const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get(`/api/admin/users${q ? `?search=${encodeURIComponent(q)}` : ''}`);
      setUsers(data || []);
    } catch (err) {
      alert('Failed to load users: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchUsers(search), 300);
    return () => clearTimeout(t);
  }, [search, fetchUsers]);

  return (
    <div data-testid="users-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-['Bebas_Neue']">Users ({users.length})</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or name…"
              className="pl-9 pr-3 py-2 bg-[#1A1A1A] border border-white/10 text-white text-sm w-64 focus:border-[#D4AF37] focus:outline-none"
              data-testid="users-search-input"
            />
          </div>
          <button
            onClick={() => fetchUsers(search)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#D4AF37] text-sm uppercase tracking-wider transition-colors"
            data-testid="users-refresh-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D0D0D] text-[#A3A3A3] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Joined</th>
                <th className="px-4 py-3 text-left">Tracks Purchased</th>
                <th className="px-4 py-3 text-left">Subscription</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#737373]">No users found.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id || u.email} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Mail size={12} className="text-[#737373]" />
                      <span className="text-white font-mono text-xs">{u.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#A3A3A3]">{u.name || '–'}</td>
                  <td className="px-4 py-3">
                    {u.role === 'admin' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#D4AF37]/15 text-[#D4AF37] uppercase tracking-wider">
                        <ShieldCheck size={12} /> Admin
                      </span>
                    ) : (
                      <span className="text-[#A3A3A3]">user</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-[#A3A3A3]">{u.purchased_track_count ?? 0}</td>
                  <td className="px-4 py-3 text-[#A3A3A3]">{u.subscription_tier || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ===== ORDERS TAB =====
export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/api/admin/orders');
      setOrders(data || []);
    } catch (err) {
      alert('Failed to load orders: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return JSON.stringify(o).toLowerCase().includes(s);
  });

  return (
    <div data-testid="orders-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-['Bebas_Neue']">Orders ({filtered.length})</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order…"
              className="pl-9 pr-3 py-2 bg-[#1A1A1A] border border-white/10 text-white text-sm w-64 focus:border-[#D4AF37] focus:outline-none"
              data-testid="orders-search-input"
            />
          </div>
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#D4AF37] text-sm uppercase tracking-wider transition-colors"
            data-testid="orders-refresh-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D0D0D] text-[#A3A3A3] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Total</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#737373]">No orders yet.</td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.order_id || o.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3 text-white font-mono text-xs">{(o.order_id || '–').slice(0, 12)}</td>
                  <td className="px-4 py-3 text-[#A3A3A3] font-mono text-xs">{o.user_email || o.user_id || '–'}</td>
                  <td className="px-4 py-3 text-[#D4AF37]">{fmtCurrency(o.total ?? o.amount, o.currency)}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-[#0D0D0D] border border-white/10">
                      {o.status || o.payment_status || '–'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ===== REVENUE TAB =====
export const RevenueTab = () => {
  const [data, setData] = useState({ transactions: [], paid_total: 0, count: 0 });
  const [filter, setFilter] = useState('all'); // all|paid|pending
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (status = '') => {
    setLoading(true);
    try {
      const { data: resp } = await axiosAuth.get(`/api/admin/transactions${status ? `?status=${status}` : ''}`);
      setData(resp || { transactions: [], paid_total: 0, count: 0 });
    } catch (err) {
      alert('Failed to load transactions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(filter === 'all' ? '' : filter);
  }, [filter, fetchData]);

  return (
    <div data-testid="revenue-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-['Bebas_Neue']">Revenue & Transactions</h2>
          <p className="text-sm text-[#A3A3A3]">
            Total paid: <span className="text-[#D4AF37] font-medium" data-testid="paid-total">{fmtCurrency(data.paid_total)}</span>
            <span className="mx-2 text-[#737373]">·</span>
            {data.count} transaction{data.count !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'paid', 'pending'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                filter === f
                  ? 'border-[#D4AF37] text-[#D4AF37]'
                  : 'border-white/15 text-[#A3A3A3] hover:border-[#D4AF37]'
              }`}
              data-testid={`revenue-filter-${f}`}
            >
              {f}
            </button>
          ))}
          <button
            onClick={() => fetchData(filter === 'all' ? '' : filter)}
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#D4AF37] text-sm uppercase tracking-wider"
            data-testid="revenue-refresh-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D0D0D] text-[#A3A3A3] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Session</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#737373]">No transactions yet.</td></tr>
              )}
              {data.transactions.map((t, i) => (
                <tr key={t.session_id || t.transaction_id || i} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#A3A3A3] whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[#A3A3A3] uppercase tracking-wider text-xs">
                      <Package size={11} /> {t.type || '–'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#D4AF37]">
                    <span className="inline-flex items-center gap-1">
                      <DollarSign size={12} /> {Number(t.amount || 0).toFixed(2)} {(t.currency || 'usd').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 text-xs uppercase tracking-wider ${
                        t.payment_status === 'paid'
                          ? 'bg-green-900/40 text-green-300'
                          : t.payment_status === 'pending'
                          ? 'bg-yellow-900/40 text-yellow-300'
                          : 'bg-[#0D0D0D] text-[#A3A3A3]'
                      }`}
                    >
                      {t.payment_status || '–'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#A3A3A3] font-mono text-xs">
                    {t.user_email || t.metadata?.user_email || (t.user_id || '–').toString().slice(0, 12)}
                  </td>
                  <td className="px-4 py-3 text-[#737373] font-mono text-xs">{(t.session_id || '–').slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminPeopleTabs = { UsersTab, OrdersTab, RevenueTab };
export default AdminPeopleTabs;
